import { FundCampaignDtoIn } from '@/models/in/FundCampaignDtoIn';

/** The fund card's campaigns endpoint; also the react-query key the tab invalidates after a save. */
export const campaignsUrl = (fundId: number) => `funds/${fundId}/management/campaigns`;

/**
 * The flag that decides whether the campaigns tab exists (HAT-1121). Matches
 * FeatureFlagKeys.FundCampaigns on the server, which also gates the endpoints themselves - so a
 * hidden tab is hidden all the way down, not merely out of sight.
 *
 * Read through the global provider: the management area belongs to no fund, so the targeting that
 * applies is by email or role.
 */
export const FUND_CAMPAIGNS_FEATURE_KEY = 'fund-campaigns';

/** yyyy-MM-dd for a date input, from the ISO date the server returns. */
export const toDateInputValue = (value?: string | null): string => (value ? value.slice(0, 10) : '');

/**
 * A campaign has no status column: whether it has started is simply whether its start date has
 * arrived. Storing that as a field would only let it go stale.
 */
export const hasStarted = (campaign: FundCampaignDtoIn, today = new Date()): boolean => {
  if (!campaign.startDate) return true;
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return new Date(campaign.startDate.slice(0, 10)) <= startOfToday;
};

/** The link as the office reads it: no scheme, no trailing slash. */
export const shortenUrl = (url: string): string => url.replace(/^https?:\/\//i, '').replace(/\/$/, '');

/**
 * Mirrors the server's rule so a bad link is refused next to the field rather than after a round
 * trip. The server stays the authority on what is stored: it does this same normalization and the
 * client sends the address as typed.
 *
 * The parser alone is not enough, and the difference is not academic: Chrome accepts
 * `https://לא כתובת` and hands back the host `xn--%20-ppeix5bd5kc`, percent-encoding the space
 * instead of refusing, while Node throws on the very same string. Anything that trusted `new URL`
 * would therefore pass free text through in a browser and reject it in a test. Hence the two
 * explicit rules below - no whitespace, and a host with a dot in it - which do not depend on
 * whose URL parser is running.
 */
export const isWebAddress = (value: string): boolean => {
  if (/\s/.test(value)) return false;

  const candidate = value.includes('://') ? value : `https://${value}`;
  try {
    const { protocol, hostname } = new URL(candidate);
    if (protocol !== 'http:' && protocol !== 'https:') return false;
    // A bare word ("hello") parses as a host; a real address has a dot and a label after it.
    return /\.[^.]{2,}$/.test(hostname) && !hostname.includes('%');
  } catch {
    return false;
  }
};
